/*
Copyright (c) 2015-2021 Gleez Technologies.

This source code is licensed under the MIT license found in the
LICENSE file in the root directory of this source tree.
*/

import { useState, useEffect, useCallback, useRef } from "react";

const VALUE = "value";
const ERROR = "error";

const REQUIRED_FIELD_ERROR = "This is required field";

/**
 * Determines a value if it's an array
 *
 * @param {array} value
 */
function isArray(value) {
  return Array.isArray(value);
}

/**
 * Determines a value if it's an object
 *
 * @param {object} value
 */
function isObject(value) {
  return value !== null && typeof value === "object";
}

function is_bool(value) {
  return typeof value === "boolean";
}

function getPropValues(stateSchema, prop) {
  if (!isObject(stateSchema)) {
    throw new Error("Invalid Parameter passed.");
  }

  // Add an gloabl error field to every schema
  if (prop === ERROR) {
    stateSchema.error = "";
  }

  return Object.keys(stateSchema).reduce((field, key) => {
    if (prop === ERROR) {
      field[key] = "";
    } else {
      field[key] = is_bool(prop) ? prop : stateSchema[key];
    }

    return field;
  }, {});
}

/**
 *
 * @param {string} value
 * @param {boolean} isRequired
 */
function isRequiredField(value, isRequired) {
  if (!value && isRequired) return REQUIRED_FIELD_ERROR;
  return "";
}

/**
 * Custom hooks to validate your Form...
 *
 * @param {object} schema model you stateSchema.
 * @param {object} validatorSchema model your validation.
 * @param {function} submitFormCallback function to be execute during form submission.
 */
function useForm(
  stateSchema = {},
  stateValidatorSchema = {},
  submitFormCallback
) {
  const [, render] = useState();
  const [state, setStateSchema] = useState(stateSchema);
  const [dirty, setDirty] = useState(getPropValues(state, false));

  const [disable, setDisable] = useState(true);
  const [isDirty, setIsDirty] = useState(false);

  const fieldsRef = useRef(getPropValues(state, VALUE));
  const errorsRef = useRef(getPropValues(state, ERROR));

  // Used to disable submit button if there's a value in errors
  // or the required field in state has no value.
  // Wrapped in useCallback to cached the function to avoid intensive memory leaked
  // in every re-render in component
  const validateErrorState = useCallback(
    () => Object.values(errorsRef.current).some((error) => error),
    []
  );

  // Get a local copy of stateSchema
  useEffect(() => {
    setStateSchema(stateSchema);

    setInitialErrorState();
  }, []); // eslint-disable-line

  // For every changed in our state this will be fired
  // To be able to disable the button
  useEffect(() => {
    if (isDirty) {
      setDisable(validateErrorState());
    }
  }, [dirty, isDirty, validateErrorState]);

  const reRender = useCallback(() => {
    render({});
  }, []);

  // Function used to validate form fields
  const validateField = useCallback(
    (name, value) => {
      const values = fieldsRef.current;
      const fieldValidator = stateValidatorSchema[name];

      // Making sure that stateValidatorSchema name is same in
      // stateSchema
      if (!fieldValidator) {
        return;
      }

      let error = "";
      error = isRequiredField(value, fieldValidator["required"]);

      // Bail out if field is not required and no value set.
      // To prevent proceeding to validator function
      if (!fieldValidator["required"] && !value) {
        return error;
      }

      // Run custom validator function
      if (error === "" && isObject(fieldValidator["validator"])) {
        // Test the function callback if the value is meet the criteria
        if (!fieldValidator["validator"]["func"](value, values)) {
          error = fieldValidator["validator"]["error"];
        }
      }

      return error;
    },
    [stateValidatorSchema]
  );

  const setInternalValue = (name, value) => {
    const fields = fieldsRef.current;

    if (name in fields) {
      fields[name] = value;
    }
  };

  const setInternalError = (name, error) => {
    const graphqlErrors = error && error.errors ? error.errors : error;
    const message =
      isArray(graphqlErrors) && graphqlErrors[0]
        ? graphqlErrors[0]
        : graphqlErrors;

    const errors = errorsRef.current;
    if (name in errors) {
      errors[name] = message;
    }
  };

  // Set Initial Error State
  // When hooks was first rendered...
  const setInitialErrorState = useCallback(() => {
    const values = fieldsRef.current;

    Object.keys(errorsRef.current).map((name) =>
      setInternalError(name, validateField(name, values[name]))
    );
  }, [validateField]);

  const setError = (name, error) => {
    setInternalError(name, error);

    reRender();
  };

  const handleOnSubmit = useCallback(
    (event) => {
      if (event) {
        event.preventDefault();
        event.persist();
      }

      // Making sure that there's no error in the state
      // before calling the submit callback function
      if (!validateErrorState()) {
        submitFormCallback(fieldsRef.current);
      }
    },
    [validateErrorState, submitFormCallback]
  );

  const processValue = useCallback(
    (name, value) => {
      setIsDirty(true);

      // unset global error on change in any field
      setInternalError("error", "");

      const error = validateField(name, value);

      setInternalError(name, error);
      setInternalValue(name, value);

      // reRender();
      setDirty((prevState) => ({ ...prevState, [name]: true }));
    },
    [validateField]
  );

  // Event handler for handling changes in input.
  const handleOnChange = useCallback(
    (event) => {
      event.persist();

      if (!event.target.name) {
        // eslint-disable-next-line no-console
        return console.warn("Missing name @", event.target);
      }

      processValue(event.target.name, event.target.value);
    },
    [processValue]
  );

  const clearError = (name) => {
    if (name) {
      if (name in errorsRef.current) {
        errorsRef.current[name] = "";
      }
    } else {
      errorsRef.current = getPropValues(state, ERROR);
    }

    reRender();
  };

  const clearErrors = () => {
    errorsRef.current = getPropValues(state, ERROR);

    reRender();
  };

  const reset = () => {
    setStateSchema(stateSchema);
    fieldsRef.current = getPropValues(state, VALUE);
    errorsRef.current = getPropValues(state, ERROR);

    reRender();
  };

  return {
    handleOnChange,
    handleOnSubmit,
    setValue: processValue,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    setError: useCallback(setError, []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    clearErrors: useCallback(clearErrors, [state]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    clearError: useCallback(clearError, [state]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    reset: useCallback(reset, [state, stateSchema]),
    values: fieldsRef.current,
    errors: errorsRef.current,
    disable,
    dirty,
  };
}

export default useForm;
